/**
 * Enter-to-advance for forms.
 *
 * Enter moves focus to the next field in the same form scope (Shift+Enter to
 * the previous one) and selects its text, so a column of inputs can be filled
 * from the keyboard — or from a phone keypad's "Go" key — without reaching
 * for the mouse. Scopes are the calculator input column, the property panel
 * body, a dialog, or any element marked data-field-scope; the document is
 * the fallback.
 */

import type { KeyboardEvent as ReactKeyboardEvent } from 'react';

const SCOPE_SELECTOR =
  '[data-field-scope], .calculator-inputs, .property-panel-body, .designer-schedule-body, [role="dialog"], form';

const FIELD_SELECTOR =
  'input:not([type="hidden"]):not([type="checkbox"]):not([type="radio"]):not([type="button"]):not([type="submit"]):not([disabled]), ' +
  'select:not([disabled]), textarea:not([disabled])';

function isReachable(el: HTMLElement): boolean {
  if (el.hidden || el.getAttribute('aria-hidden') === 'true') return false;
  if (el.closest('[hidden], [aria-hidden="true"]')) return false;
  if (el.tabIndex < 0 && el.getAttribute('tabindex') === '-1') return false;
  return true;
}

/** Move focus to the adjacent field; returns the element focused, if any. */
export function focusAdjacentField(from: HTMLElement, dir: 1 | -1): HTMLElement | null {
  const scope = (from.closest(SCOPE_SELECTOR) as HTMLElement | null) ?? from.ownerDocument.body;
  const fields = Array.from(scope.querySelectorAll<HTMLElement>(FIELD_SELECTOR)).filter(isReachable);
  const i = fields.indexOf(from);
  if (i < 0) return null;
  const next = fields[i + dir];
  if (!next) return null;
  next.focus();
  if (next instanceof HTMLInputElement || next instanceof HTMLTextAreaElement) {
    try {
      next.select();
    } catch {
      /* type=number refuses select() in some engines — focus is enough */
    }
  }
  return next;
}

/**
 * Keydown helper for field components: on Enter (Shift+Enter) advances
 * (retreats) and returns true; any other key returns false untouched.
 */
export function handleEnterNavigation(ev: ReactKeyboardEvent<HTMLElement>): boolean {
  if (ev.key !== 'Enter' || ev.ctrlKey || ev.metaKey || ev.altKey) return false;
  if (ev.nativeEvent.isComposing) return false;
  const moved = focusAdjacentField(ev.currentTarget, ev.shiftKey ? -1 : 1);
  if (moved) ev.preventDefault();
  return moved !== null;
}
